import "zod/compile";
import { z } from "zod";

/**
 * X12 849 footer AMT qualifiers that carry tax.
 *
 * Shared across the backend tax extractor (`edi849TaxStrategies.ts`) and the
 * frontend raw-EDI header parser (`ediHeaderParser.ts`) so the two layers
 * reference a single source of truth and tax parsing can't drift between them.
 * (`AMT*1` = per-line amount and `AMT*2` = batch total are NOT tax.)
 */
export const TAX_AMT_QUALIFIER = {
  /** HST (single-rate HST provinces) */
  COMBINED: "T",
  /** GST (Goods & Services Tax) */
  GST: "GT",
  /** FST / HST */
  FST: "F7",
  /** PST / QST (Provincial Sales Tax) */
  PST: "ST",
} as const;

/**
 * Invoice master data returned with an E844SD transaction detail.
 * Sourced from rexel_invoice_master, joined by productNumber + company.
 * Dates are stored as 6-digit YYMMDD integers (e.g. 260317 = 2026-03-17).
 */
export interface InvoiceMasterData {
  productNumber: string | null;
  invoiceNumber: string;
  invoiceDate: number | null;
  invoiceDescription: string | null;
  amount: number | null;
  cost: number | null;
  poCost: number | null;
  quantity: number | null;
  division: string | null;
  location: string | null;
  supplierNumber: string | null;
  poNumber: string | null;
}

/**
 * Hardcoded filter options for S&D pending transactions.
 *
 * Shared between backend (getFilterOptionsProcedure) and frontend
 * (AvailableTransactionsSection). Update when new companies, divisions,
 * ship types, or trading partners are added.
 */
export const SD_FILTER_OPTIONS = {
  company: ["Q", "R", "W"],
  division: ["+", "A", "B", "E", "H", "J", "O", "R", "T"],
  ship_type: ["1", "2", "3", "I1", "I2", "I3"],
  rebate_type: ["C", "M", "P"],
  selling_location: [
    "!",
    '"',
    "#",
    "$",
    "%",
    "&",
    "(",
    ")",
    "*",
    "+",
    "-",
    ";",
    "<",
    "=",
    ">",
    "0",
    "1",
    "2",
    "3",
    "4",
    "6",
    "7",
    "8",
    "9",
    "@",
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
    "a",
    "c",
    "d",
    "e",
    "f",
    "h",
    "j",
    "k",
    "l",
    "m",
    "n",
    "q",
    "r",
    "t",
    "u",
    "v",
    "w",
    "y",
  ],
  trading_partner: [
    "001288364I",
    "201484300",
    "3M",
    "ABB",
    "ANAMET",
    "APPLETON",
    "BELDEN",
    "BUSSMANN",
    "COMMSCOPE(CAD)",
    "COMMSCOPE(USD)",
    "COOPERWIRING",
    "CRCCANADA",
    "CRCROUSE",
    "EATON",
    "EGSELECTRICAL",
    "ERICO",
    "HAMMOND",
    "HAMMONDPOWER",
    "HOFFMAN",
    "HUBBELL",
    "IPEXEDI",
    "LEDVANCE",
    "LEVITON",
    "LITELINE",
    "NONEDIDFT",
    "NVENT",
    "OMRON",
    "OUELLETCAN",
    "PANDUITEDI",
    "PHOENIXEDIP",
    "RITTAL",
    "ROCKWELL",
    "SCHNEIDER",
    "STANDARDPROD",
    "STELPRODESIGN",
    "SUPERIORESSEX",
    "TESELECTRIC",
    "TYCO",
  ],
};

/**
 * `data.code` on the CONFLICT the post-send "Link a VDM" attach throws when the
 * selected transactions are in a different tax province from the VDM, but one
 * a Province override may cross (AB ↔ BC), and the caller may grant it. The
 * dialog answers it with a "Link Anyway" warning that resends the link with
 * `provinceOverride: true`.
 */
export const VDM_PROVINCE_OVERRIDE_REQUIRED_CODE =
  "VDM_PROVINCE_OVERRIDE_REQUIRED" as const;

export const VdmProvinceOverrideRequiredDataSchema = z.object({
  code: z.literal(VDM_PROVINCE_OVERRIDE_REQUIRED_CODE),
  /** The VDM as users know it: its AS400 number. */
  vdmLabel: z.string(),
  /** The province the VDM is billed to. */
  vdmProvince: z.string(),
  /** The distinct provinces of the transactions that would be crossed. */
  transactionProvinces: z.array(z.string()),
  /** How many selected transactions are in another province. */
  count: z.int(),
});

export type VdmProvinceOverrideRequiredData = z.infer<
  typeof VdmProvinceOverrideRequiredDataSchema
>;

/** Whether Rexel can fix the cause of an 849 rejection (per the S&D team). */
export type Edi849RejectControllability = "controllable" | "uncontrollable";

export interface Edi849RejectReason {
  description: string;
  controllability?: Edi849RejectControllability;
}

/**
 * X12 element 901 (Reject Reason Code) as it applies to an 849 AAA segment,
 * from the S&D team's "Rejection Reason Code EDI849" sheet (SD-97, 2026-11-03).
 * Suppliers send only the code; these are the generic meanings, the same for
 * every supplier. The sheet also lists element 901's healthcare and rail codes
 * (41 and up, and the two-letter codes); no S&D supplier sends those, so only
 * 01–37 and `ZZ` are kept.
 */
export const EDI_849_REJECT_REASONS: Readonly<
  Record<string, Edi849RejectReason>
> = {
  "01": {
    description: "Price Authorization Invalid",
    controllability: "controllable",
  },
  "02": {
    description: "Price Authorization Expired",
    controllability: "controllable",
  },
  "03": {
    description: "Product not on the price authorization",
    controllability: "controllable",
  },
  "04": { description: "Authorized Quantity Exceeded" },
  "05": { description: "Zero Balance" },
  "06": {
    description: "Special Cost Incorrect",
    controllability: "controllable",
  },
  "07": { description: "Catalog Cost Incorrect" },
  "08": {
    description: "Invalid Ship Location",
    controllability: "uncontrollable",
  },
  "09": { description: "No Credit Allowed" },
  "10": { description: "Administrative Cancellation" },
  "11": { description: "Invalid Debit Number" },
  "12": { description: "Duplicate Sequence Number" },
  "13": { description: "Not Valid for Price Protection" },
  "14": { description: "Invalid part number", controllability: "controllable" },
  "15": { description: "Required application data missing" },
  "16": { description: "Unit resale higher than authorized" },
  "17": { description: "Negotiated price was not less than book price" },
  "18": { description: "Ship date must not be after current date" },
  "19": {
    description: "Ship date cannot be prior to price authorization issue date",
  },
  "20": {
    description:
      "Ship date should not be before price authorization date (for rebills)",
  },
  "21": { description: "Price authorization is a rebill type" },
  "23": { description: "Price authorization has been deleted" },
  "24": { description: "Price authorization used on a sales order" },
  "25": { description: "Disposition pending vendor review" },
  "26": {
    description: "Invalid Customer Number",
    controllability: "controllable",
  },
  "27": { description: "Invalid Ship Date", controllability: "controllable" },
  "28": { description: "Duplicate Invoice Number" },
  "29": { description: "Claim Submitted Past Exercise Period" },
  "30": { description: "Invalid Meet Competition Cost" },
  "31": { description: "Invalid Book Cost" },
  "32": { description: "Input Incomplete" },
  "33": { description: "Input Errors" },
  "34": { description: "No Coverage" },
  "35": { description: "Out of Network" },
  "36": { description: "Testing not Included" },
  "37": {
    description:
      "Request Forwarded To and Decision Response Forthcoming From an External Review Organization",
  },
  ZZ: { description: "Mutually Defined" },
};

/**
 * The meaning of an 849 reject code, or null when the list has none. A one-digit
 * code is read as its two-digit form ("1" → "01"), since parsers and suppliers
 * drop the leading zero inconsistently.
 */
export function edi849RejectReason(
  code: string | null | undefined,
): Edi849RejectReason | null {
  const trimmed = code?.trim().toUpperCase();
  if (!trimmed) return null;
  const key = /^\d$/.test(trimmed) ? `0${trimmed}` : trimmed;
  return EDI_849_REJECT_REASONS[key] ?? null;
}
